import {
  useCallback,
  useMemo,
  type MutableRefObject,
  type Dispatch,
  type SetStateAction
} from 'react';
import type { AnyAction } from '@reduxjs/toolkit';
import { clearUnsavedFeaturesFromSessionStorage } from '../utils/unsavedFeaturesStorage';
import { mergeViewStateSearchParams } from 'utils/sharedViewMode';
import {
  getNextSlideoutMomentId,
  getPreviousSlideoutMomentId,
  type SlideoutMomentNavigationFilters,
  type SlideoutMomentNavigationParams
} from '../utils/slideoutMomentNavigation';
import type { SlideOutPanelCard } from '../types/slideOutPanel.types';

export type UseSlideoutNavigationParams = {
  card: SlideOutPanelCard | null | undefined;
  hasUnsavedChanges: () => boolean;
  setShowUnsavedWarning: (show: boolean) => void;
  pendingCloseAction: MutableRefObject<(() => void) | null>;
  overviewHasUnsavedChanges: MutableRefObject<boolean>;
  setOverviewResetTrigger: Dispatch<SetStateAction<number>>;
  setIsInitialLoading: (loading: boolean) => void;
  isChevronNavigationRef: MutableRefObject<boolean>;
  setTargetFeatureId: (id: string | null) => void;
  setTargetProductId: (id: string | null) => void;
  location: { pathname: string; search: string };
  navigate: (url: string, options?: { replace?: boolean }) => void;
  isCommentPanelOpen: boolean;
  dispatch: Dispatch<AnyAction>;
  fetchComments: (issueId: string) => AnyAction;
  releases?: { releaseScheduled?: unknown[] };
  filteredReleases?: unknown[];
  navigationFilters: SlideoutMomentNavigationFilters;
  tierOptions: string[];
  getCurrentTier: () => string;
};

/**
 * Chevron / keyboard moment navigation with unsaved-changes guard.
 * Extracted from SlideOutPanel/index.tsx (Part 2A modularisation).
 */
export function useSlideoutNavigation({
  card,
  hasUnsavedChanges,
  setShowUnsavedWarning,
  pendingCloseAction,
  overviewHasUnsavedChanges,
  setOverviewResetTrigger,
  setIsInitialLoading,
  isChevronNavigationRef,
  setTargetFeatureId,
  setTargetProductId,
  location,
  navigate,
  isCommentPanelOpen,
  dispatch,
  fetchComments,
  releases,
  filteredReleases,
  navigationFilters,
  tierOptions,
  getCurrentTier
}: UseSlideoutNavigationParams) {
  const navigationParams = useMemo(
    (): SlideoutMomentNavigationParams => ({
      releases,
      filteredReleases,
      filters: navigationFilters,
      tierOptions,
      getCurrentTier,
      cardTierFallback: (card as any)?.tier,
      cardGeneralInfoTierFallback: (card as any)?.generalInfo?.tier
    }),
    [
      releases,
      filteredReleases,
      navigationFilters,
      tierOptions,
      getCurrentTier,
      card
    ]
  );

  const performNavigateToMoment = useCallback(
    (targetId: string) => {
      if (!card?.issueId) return;

      if (targetId === card.issueId) {
        console.error('⚠️ Prevented navigation to same moment ID:', targetId);
        return;
      }

      clearUnsavedFeaturesFromSessionStorage();

      overviewHasUnsavedChanges.current = false;

      setOverviewResetTrigger(prev => prev + 1);

      setIsInitialLoading(true);

      isChevronNavigationRef.current = true;

      setTargetFeatureId(null);
      setTargetProductId(null);

      const searchParams = mergeViewStateSearchParams(location.search, '');
      searchParams.delete('featureId');
      searchParams.delete('productId');
      searchParams.delete('phaseId');

      const isFromMilestones = location.pathname.includes('/milestones');
      const baseRoute = isFromMilestones ? '/milestones/moments' : '/moments';
      const momentUrl = `${baseRoute}/${encodeURIComponent(targetId)}`;
      const fullUrl = searchParams.toString()
        ? `${momentUrl}?${searchParams.toString()}`
        : momentUrl;

      navigate(fullUrl, { replace: false });

      if (isCommentPanelOpen) {
        dispatch(fetchComments(targetId));
      }
    },
    [
      card?.issueId,
      overviewHasUnsavedChanges,
      setOverviewResetTrigger,
      setIsInitialLoading,
      isChevronNavigationRef,
      setTargetFeatureId,
      setTargetProductId,
      location.search,
      location.pathname,
      navigate,
      isCommentPanelOpen,
      dispatch,
      fetchComments
    ]
  );

  const performNavigateNext = useCallback(() => {
    if (!card?.issueId) return;

    const nextId = getNextSlideoutMomentId(card.issueId, navigationParams);
    if (nextId) {
      performNavigateToMoment(nextId);
    }
  }, [card?.issueId, navigationParams, performNavigateToMoment]);

  const performNavigatePrevious = useCallback(() => {
    if (!card?.issueId) return;

    const previousId = getPreviousSlideoutMomentId(
      card.issueId,
      navigationParams
    );
    if (previousId) {
      performNavigateToMoment(previousId);
    }
  }, [card?.issueId, navigationParams, performNavigateToMoment]);

  const handleNavigateNext = useCallback(() => {
    if (!card?.issueId) return;

    if (hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
      pendingCloseAction.current = () => performNavigateNext();
      return;
    }

    performNavigateNext();
  }, [
    card?.issueId,
    hasUnsavedChanges,
    setShowUnsavedWarning,
    pendingCloseAction,
    performNavigateNext
  ]);

  const handleNavigatePrevious = useCallback(() => {
    if (!card?.issueId) return;

    if (hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
      pendingCloseAction.current = () => performNavigatePrevious();
      return;
    }

    performNavigatePrevious();
  }, [
    card?.issueId,
    hasUnsavedChanges,
    setShowUnsavedWarning,
    pendingCloseAction,
    performNavigatePrevious
  ]);

  return {
    handleNavigateNext,
    handleNavigatePrevious,
    performNavigateNext,
    performNavigatePrevious,
    getNextMomentId: (currentIssueId: string) =>
      getNextSlideoutMomentId(currentIssueId, navigationParams),
    getPreviousMomentId: (currentIssueId: string) =>
      getPreviousSlideoutMomentId(currentIssueId, navigationParams)
  };
}
